import * as React from 'react'
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu'

import { cn } from '../../lib/utils'

// When the menu opens under the pointer, the same right-button (or ctrl-click) release can
// land on an item and pick it. Ignore only that release; normal clicks and keys always work.
const OpenedAt = React.createContext({ current: 0 })
const GUARD_MS = 250

// Same look as the dropdown menus. Non-modal: a modal menu that opens a dialog
// leaves the page unclickable once the dialog closes.
const ContextMenu = ({ modal = false, onOpenChange, ...props }) => {
  const openedAt = React.useRef(0)
  return (
    <OpenedAt.Provider value={openedAt}>
      <ContextMenuPrimitive.Root
        modal={modal}
        onOpenChange={(open) => {
          if (open) openedAt.current = Date.now()
          onOpenChange?.(open)
        }}
        {...props}
      />
    </OpenedAt.Provider>
  )
}
const ContextMenuTrigger = ContextMenuPrimitive.Trigger

const ContextMenuContent = React.forwardRef(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(
        'z-50 min-w-[13rem] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-100',
        className
      )}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
))
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName

const ContextMenuItem = React.forwardRef(({ className, onSelect, onPointerUp, ...props }, ref) => {
  const openedAt = React.useContext(OpenedAt)
  const openingRelease = React.useRef(false)
  return (
  <ContextMenuPrimitive.Item
    ref={ref}
    onPointerUp={(event) => {
      openingRelease.current = (event.button === 2 || event.ctrlKey) && Date.now() - openedAt.current < GUARD_MS
      onPointerUp?.(event)
    }}
    onSelect={(event) => {
      const blocked = openingRelease.current
      openingRelease.current = false
      if (blocked) {
        event.preventDefault() // the release of the click that opened the menu: keep it open
        return
      }
      onSelect?.(event)
    }}
    className={cn(
      'relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-[13.5px] outline-none focus:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  />
  )
})
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName

const ContextMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
))
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName

const ContextMenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Label ref={ref} className={cn('truncate px-2 py-1.5 text-xs font-medium text-muted-foreground', className)} {...props} />
))
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName

/** Render the same {label, icon, run, danger} | '-' entries the ⋮ menus use. */
const ContextMenuEntries = ({ entries }) => entries.map((entry, i) => (entry === '-'
  ? <ContextMenuSeparator key={`sep-${i}`} />
  : (
    <ContextMenuItem key={entry.label} onSelect={entry.run} className={entry.danger ? 'text-destructive focus:text-destructive' : ''}>
      <entry.icon className="h-4 w-4" aria-hidden="true" /> {entry.label}
    </ContextMenuItem>
  )))

export { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuLabel, ContextMenuEntries }
